<template>
  <NForm class="content-selector-external-links h-full flex flex-col">
    <NGrid class="h-full overflow-hidden mt-3" cols="12" item-responsive>
      <NGridItem class="h-full overflow-auto" :span="'12 650:7 1200:8 1400:9'">
        <div class="mr-3 h-full">
          <NFormItem
            :label="t('canvasUI.contentSelector.externalLinks.addImageOrVideoLink')"
            path="external_url"
            :rule="{
              trigger: ['input', 'blur'],
            }"
          >
            <NInput v-model:value="url" clearable placeholder="" @keyup.enter="onAddUrl">
              <template v-if="isCrmShapeEnabled" #suffix>
                <CDynamicValueTreeSelect
                  :allow-arrays="false"
                  :allow-multiple="false"
                  :data="crmShape"
                  @select="onDynamicValueSelect"
                />
              </template>
            </NInput>
          </NFormItem>
          <div class="flex justify-end w-full">
            <CButton type="primary" @click="onAddUrl">
              {{ t('canvasUI.common.add') }}
            </CButton>
          </div>
          <MediaPreview class="mt-3" :url="parsedUrl" />
        </div>
      </NGridItem>
      <NGridItem class="h-full overflow-auto" :span="'12 650:5 1200:4 1400:3'">
        <SelectionPanel />
      </NGridItem>
    </NGrid>
  </NForm>
</template>

<script setup lang="ts">
import { NGridItem, NGrid, NFormItem, NInput, NForm } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import { ref, computed, inject, type ComputedRef } from 'vue'

import CButton from '@lib/components/CButton/CButton.vue'
import CDynamicValueTreeSelect from '@lib/components/CRichTextEditor/components/CDynamicValueTreeSelect.vue'
import SelectionPanel from '@content-selector/components/Content/SelectionPanel/SelectionPanel.vue'
import MediaPreview from '@content-selector/components/ui/MediaPreview.vue'
import { getMediaType } from '@content-selector/util/media-type.util'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import useCrmShapeWithSectionContext from '@canvas-builder/composables/useCrmShapeWithSectionContext'
import { renderTemplate } from '@lib/util/handlebars'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const emit = defineEmits<{
  add: [{ url: string; content_type: 'image' | 'video' }]
}>()

const url = ref('')
const { t } = useI18n()

// Inject LaunchDarkly flags
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

// Use composables for context building
const { crmShape } = useCrmShape()
const { fullContext } = useCrmShapeWithSectionContext()

// Check if CRM shape feature is enabled
const isCrmShapeEnabled = computed(() => launchDarkly.value?.enable_crm_shape_for_external_links ?? false)

// Parse URL with dynamic values before passing to MediaPreview
const parsedUrl = computed(() => {
  if (!url.value.trim()) return url.value

  // Only parse templates if CRM shape feature is enabled
  if (!isCrmShapeEnabled.value) return url.value

  try {
    return renderTemplate(url.value, fullContext.value)
  } catch (error) {
    console.error('Error parsing URL template:', error)
    return url.value
  }
})

function hasDynamicValues(url: string): boolean {
  return url.includes('{{') && url.includes('}}')
}

function onDynamicValueSelect(value: string) {
  // Append the selected dynamic value to the current URL
  url.value = url.value + value
}

async function onAddUrl() {
  if (url.value) {
    // First try to get media type from parsed URL for validation
    const type = await getMediaType(parsedUrl.value)
    const shouldBypassValidation = isCrmShapeEnabled.value && hasDynamicValues(url.value)

    if (shouldBypassValidation || type) {
      // Store the original URL with dynamic values, not the parsed version
      emit('add', { url: url.value, content_type: type ?? 'image' })
      url.value = ''
    }
  }
}
</script>

<style scoped>
:deep(.dynamic-value-tree-select__trigger) {
  margin-right: -16px;
}
</style>
